import { motion } from "framer-motion";
import { Cog, Wrench, Droplets, Package, ArrowUpRight } from "lucide-react";
import { scrollToId } from "./nav";

const PRODUCTS = [
  {
    icon: Cog,
    title: "Комплектующие для промышленного оборудования",
    desc: "Подшипники, редукторы, электротехническая продукция, приводы, гидравлика и пневматика.",
    image:
      "https://images.unsplash.com/photo-1524514587686-e2909d726e9b",
    tags: ["Подшипники", "Редукторы", "Электрика", "Гидравлика"],
  },
  {
    icon: Wrench,
    title: "Промышленные запасные части",
    desc: "Запчасти для станочного парка, спецтехники, транспорта и производственных линий.",
    image:
      "https://images.unsplash.com/photo-1763738173457-2a874a207215",
    tags: ["Станки", "Спецтехника", "Производственные линии"],
  },
  {
    icon: Droplets,
    title: "Горюче-смазочные материалы (ГСМ)",
    desc: "Дизельное топливо, моторные и индустриальные масла, смазки, охлаждающие и рабочие жидкости.",
    image:
      "https://images.unsplash.com/photo-1780882899461-0b158f457b44",
    tags: ["ДТ", "Масла", "Смазки", "Присадки"],
  },
  {
    icon: Package,
    title: "Хозяйственные и промышленные товары",
    desc: "Расходные материалы, инвентарь, СИЗ, инструмент и товары для обеспечения производства.",
    image:
      "https://images.pexels.com/photos/14674132/pexels-photo-14674132.jpeg",
    tags: ["СИЗ", "Инструмент", "Инвентарь", "Расходники"],
  },
];

export default function Products() {
  return (
    <section
      id="products"
      data-testid="products-section"
      className="section bg-[#F8FAFC] relative"
    >
      <div className="container-x">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div className="max-w-2xl">
            <div className="eyebrow mb-6">Базовые направления поставок</div>
            <h2 className="font-display text-[#0A192F] text-3xl sm:text-4xl lg:text-5xl font-semibold leading-[1.1] tracking-tight">
              Что&nbsp;мы&nbsp;поставляем
            </h2>
          </div>
          <p className="text-slate-600 max-w-md text-base lg:text-lg">
            Базовые направления снабжения предприятий. Ниже — расширенные направления:
            промышленная мебель, фурнитура, электродвигатели и электромеханика.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
          {PRODUCTS.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.article
                key={p.title}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                data-testid={`product-card-${i}`}
                className="group bg-white border border-slate-200 overflow-hidden hover:border-[#0A192F] transition-all"
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A192F]/70 via-[#0A192F]/10 to-transparent" />
                  <div className="absolute top-4 left-4 w-12 h-12 bg-[#FF6B00] flex items-center justify-center">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="absolute top-4 right-4 text-white/80 text-xs font-medium tabular-nums">
                    0{i + 1}
                  </div>
                </div>

                <div className="p-6 lg:p-8">
                  <h3 className="font-display text-[#0A192F] text-xl lg:text-2xl font-semibold leading-tight">
                    {p.title}
                  </h3>
                  <p className="mt-3 text-slate-600 text-base leading-relaxed">
                    {p.desc}
                  </p>

                  <div className="mt-5 flex flex-wrap gap-2">
                    {p.tags.map((t) => (
                      <span
                        key={t}
                        className="text-xs uppercase tracking-wider font-medium text-slate-500 border border-slate-200 px-3 py-1"
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  <button
                    onClick={() => scrollToId("request")}
                    data-testid={`product-cta-${i}`}
                    className="mt-8 group/btn inline-flex items-center gap-2 text-[#0A192F] font-medium text-sm hover:text-[#FF6B00] transition-colors"
                  >
                    Запросить КП
                    <ArrowUpRight className="w-4 h-4 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5 transition-transform" />
                  </button>
                </div>
              </motion.article>
            );
          })}
        </div>

        <div className="mt-14 lg:mt-20 border border-dashed border-slate-300 p-8 lg:p-10 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
          <div>
            <div className="font-display text-[#0A192F] text-xl lg:text-2xl font-semibold">
              Не&nbsp;нашли нужную позицию?
            </div>
            <div className="mt-2 text-slate-600">
              Отправьте техническое задание или список — подберём аналоги и просчитаем стоимость.
            </div>
          </div>
          <button
            onClick={() => scrollToId("request")}
            data-testid="products-general-cta"
            className="shrink-0 inline-flex items-center gap-2 bg-[#0A192F] hover:bg-[#122544] text-white px-8 py-4 font-medium transition-colors"
          >
            Отправить запрос
            <ArrowUpRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
